import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Package, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_asset-tracker-117/artifacts/sfo88lq2_%E6%B5%8E%E5%8D%97%E6%B2%83%E5%BE%B7LOGO-JPG%E7%89%88.jpg";

const PasswordUpdatePage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  // All authenticated users can update their password
  useEffect(() => {
    if (!user) {
      toast.error("请先登录");
      navigate("/login");
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!formData.current_password.trim() || !formData.new_password.trim()) {
      toast.error("请填写当前密码和新密码");
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error("新密码和确认密码不匹配");
      return;
    }

    if (formData.new_password.length < 3) {
      toast.error("新密码长度至少3位");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/users/password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      
      toast.success("密码更新成功！");
      setFormData({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
    } catch (error) {
      if (error.response?.data?.detail === "Current password is incorrect") {
        toast.error("当前密码不正确");
      } else {
        toast.error(error.response?.data?.detail || "密码更新失败");
      }
    } finally {
      setLoading(false);
    }
  };

  // All authenticated users can access password update

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回首页
          </Button>
          <div className="flex items-center space-x-4">
            <img 
              src={LOGO_URL} 
              alt="Company Logo" 
              className="h-8 w-8 object-contain rounded bg-white p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="w-8 h-8 bg-slate-700 rounded items-center justify-center" style={{display: 'none'}}>
              <Package className="w-4 h-4 text-slate-300" />
            </div>
            <h1 className="text-lg font-semibold">更新密码</h1>
          </div>
        </div>
      </div>

      {/* Password Update Form */}
      <div className="max-w-md mx-auto px-6 py-8">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-slate-600" />
              </div>
            </div>
            <CardTitle className="text-xl">更新密码</CardTitle>
            <CardDescription>
              为了账户安全，请定期更新您的密码
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <Label htmlFor="current_password">当前密码 *</Label>
                <Input
                  id="current_password"
                  name="current_password"
                  type="password"
                  placeholder="输入当前密码"
                  value={formData.current_password}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="new_password">新密码 *</Label>
                <Input
                  id="new_password"
                  name="new_password"
                  type="password"
                  placeholder="输入新密码"
                  value={formData.new_password}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="confirm_password">确认新密码 *</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  placeholder="再次输入新密码"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? "更新中..." : "更新密码"}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => navigate("/")}
                  className="flex-1"
                >
                  取消
                </Button>
              </div>
            </form>
            
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <h3 className="text-sm font-medium text-slate-700 mb-2">密码安全提示:</h3>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• 使用至少8位字符的密码</li>
                <li>• 包含字母、数字和特殊字符</li>
                <li>• 不要使用容易猜测的密码</li>
                <li>• 定期更新密码以确保账户安全</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PasswordUpdatePage;